import React from 'react';
import { styled } from '@linaria/react';

import { COURSES } from '@/constants';

import MaxWidthWrapper, {
  Props as MaxWidthWrapperProps,
} from '@/components/MaxWidthWrapper';
import Logo from '@/components/Logo';
import Link from '@/components/Link';
import IconRSS from '@/components/Icons/IconRSS';
import IconSearch from '@/components/Icons/IconSearch';
import IconColorMode from '@/components/Icons/IconColorMode';
import IconSpeaker from '@/components/Icons/IconSpeaker';
import IconBluesky from '@/components/Icons/IconBluesky';

import Affirmation from './Affirmation';
import CategoryList from './FooterCategoryList';
import FooterLink from './FooterLink';
import IconLink from './IconLink';
import NewsletterForm from './FooterNewsletterForm';

interface Props extends MaxWidthWrapperProps {
  includeColorThemeToggle?: boolean;
  includeNewsletterSignup?: boolean;
}

function SiteFooter({
  includeColorThemeToggle,
  includeNewsletterSignup,
  ...delegated
}: Props) {
  const Wrapper = includeNewsletterSignup
    ? WrapperWithEmailSignup
    : WrapperWithoutEmailSignup;

  return (
    <Wrapper {...delegated}>
      <Intro
        style={{
          // when we're rendering without the email signup, do some optical alignment to create the illustion of baseline alignment.
          transform: 'translateY(-0.5rem)',
        }}
      >
        <Logo />
        <StyledAffirmation />
      </Intro>
      {includeNewsletterSignup && <StyledNewsletterForm />}
      <Links>
        <Cats>
          <MiniHeading>Browse By Category</MiniHeading>
          <CategoryList />
        </Cats>
        <Courses>
          <MiniHeading>Interactive Courses</MiniHeading>
          <LinkList>
            {COURSES.map((course) => (
              <li key={course.slug}>
                <FooterLink href={course.href}>
                  {course.shortTitle || course.title}
                </FooterLink>
              </li>
            ))}
          </LinkList>
        </Courses>
        <Misc>
          <MiniHeading>General</MiniHeading>
          <LinkList>
            <li>
              <FooterLink href="/about-josh">About Josh</FooterLink>
            </li>
            <li>
              <FooterLink href="/blog/how-i-built-my-blog-v2">
                About This Blog
              </FooterLink>
            </li>
            <li>
              <FooterLink href="/contact">Contact</FooterLink>
            </li>
          </LinkList>
        </Misc>
        <Social>
          <IconSearch size={20} />
          <IconSpeaker size={20} />
          {includeColorThemeToggle && <IconColorMode size={20} />}
          <IconRSS size={20} />
          <IconBluesky />
          <IconLink
            alt="Josh's Github profile"
            href="https://github.com/joshwcomeau/"
            icon="github"
          />
          <IconLink
            alt="Josh's LinkedIn profile"
            href="https://www.linkedin.com/in/joshwcomeau/"
            icon="linkedin"
          />
        </Social>
      </Links>
      <Legal>
        <Copyright>
          © 2018-present Joshua Comeau. All Rights Reserved.
        </Copyright>
        <LegalLinksList>
          <LegalListItem>
            <LegalLink href="https://courses.joshwcomeau.com/terms">
              Terms of Use
            </LegalLink>
          </LegalListItem>
          <LegalListItem>
            <LegalLink href="https://courses.joshwcomeau.com/privacy">
              Privacy Policy
            </LegalLink>
          </LegalListItem>
          <LegalListItem>
            <LegalLink href="https://courses.joshwcomeau.com/code-of-conduct">
              Code of Conduct
            </LegalLink>
          </LegalListItem>
        </LegalLinksList>
      </Legal>
    </Wrapper>
  );
}

const BaseWrapper = styled(MaxWidthWrapper)`
  /* Needs to use Positioned Layout to sit above decorative clouds */
  --icon-hover-color: var(--color-text);
  position: relative;
  display: grid;
  grid-template-columns: 24rem 1fr;
  gap: 32px 96px;
  justify-content: space-between;
  align-items: end;
  padding-bottom: 40px;
  font-size: 0.875rem;

  @media (max-width: 55rem) {
    grid-template-columns: 22rem 1fr;
    gap: 32px 32px;
  }

  @media (max-width: 48rem) {
    grid-template-columns: 1fr;
    justify-content: revert;
    align-items: revert;
  }
`;

const WrapperWithoutEmailSignup = styled(BaseWrapper)`
  grid-template-areas:
    'intro links'
    'legal links';

  @media (max-width: 48rem) {
    grid-template-areas:
      'intro'
      'links'
      'legal';
  }
`;

const WrapperWithEmailSignup = styled(BaseWrapper)`
  grid-template-areas:
    'intro links'
    'email links'
    'legal links';

  @media (max-width: 48rem) {
    grid-template-areas:
      'intro'
      'email'
      'links'
      'legal';
  }
`;

const Intro = styled.div`
  grid-area: intro;
`;

const StyledNewsletterForm = styled(NewsletterForm)`
  grid-area: email;
`;

const StyledAffirmation = styled(Affirmation)`
  margin-top: 8px;
`;

const Links = styled.div`
  grid-area: links;
  display: grid;
  grid-template-areas:
    'cats courses misc'
    'cats social social';
  grid-template-columns: 10rem 10rem 6.5rem;
  justify-content: space-between;
  gap: 16px 64px;

  @media (max-width: 67.125rem) {
    grid-template-areas:
      'courses misc'
      'social social';
    grid-template-columns: 10rem 6.5rem;
    justify-content: end;
  }

  @media (max-width: 48rem) {
    justify-content: start;
    grid-template-columns: 10rem 1fr;
    gap: 32px 32px;
    /* On tablets, the footer looks too sparse if it stretches to fill the entire space. Shrink to fill a normal-looking amount of space. */
    max-width: 20.625rem;
  }

  @media (max-width: 28rem) {
    /* On larger iphones, the artificial truncation of 'max-width' feels awkward, since there's only a tiny bit more space on the right than on the left. Undo the clamp. */
    max-width: revert;
  }

  @media (max-width: 21rem) {
    grid-template-areas:
      'courses'
      'misc'
      'social';
    grid-template-columns: 1fr;
  }
`;

const Cats = styled.div`
  grid-area: cats;

  @media (max-width: 67.125rem) {
    display: none;
  }
`;
const Courses = styled.div`
  grid-area: courses;
`;
const Misc = styled.div`
  grid-area: misc;
`;
const Social = styled.div`
  grid-area: social;
  display: flex;
  width: 100%;
  justify-content: space-between;
  color: var(--secondary-text);
  transform: translateY(40%);

  @media (max-width: 21rem) {
    justify-content: space-between;
    gap: 0px;
  }
`;

const MiniHeading = styled.h2`
  display: block;
  margin: 0;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  font-size: inherit;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  color: var(--secondary-text);

  @media (max-width: 21rem) {
    text-align: center;
  }

  ${Misc} & {
    text-align: right;

    @media (max-width: 21rem) {
      text-align: center;
    }
  }
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  list-style-type: none;

  @media (max-width: 21rem) {
    text-align: center;
  }

  ${Misc} & {
    text-align: right;

    @media (max-width: 21rem) {
      text-align: center;
    }
  }
`;

const Legal = styled.div`
  grid-area: legal;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
  color: var(--secondary-text);
  font-size: 0.75rem;
`;
const Copyright = styled.p`
  display: block;
  margin: 0;
`;
const LegalLinksList = styled.ul`
  display: flex;
  gap: 16px;
  padding: 0;
  list-style-type: none;
`;
const LegalListItem = styled.li`
  position: relative;
`;

const LegalLink = styled(Link)`
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

export default SiteFooter;
